import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { computeEstimate, parseDates, parseWeekdays, zonedToInstant, WAREHOUSE_TZ } from '@theme/ssc-delivery-logic';

/**
 * SSC delivery estimate under the buy buttons (snippets/ssc-delivery-estimate.liquid).
 * - Country: Shopify's GeoIP (browsing context suggestions, via the shared @theme/ssc-ship-check,
 *   cached for the session), else `window.Shopify.country`, else "other country" (no date).
 * - Lines from assets/ssc-delivery-logic.js on the Berlin clock; re-evaluated on every minute
 *   boundary, so the state flips at the cut-off without a reload.
 * - Hidden when the selected variant is unavailable, ships nothing or sells as preorder.
 * - QA (only with data-qa, i.e. theme editor or a non-live theme): ?ssc_now= (Berlin wall clock),
 *   ?ssc_country=, ?ssc_mode=sameday|safe, ?ssc_delivery_test=1 runs the console tests.
 *
 * @typedef {{ main: HTMLElement, sub: HTMLElement, config: HTMLScriptElement }} Refs
 * @extends {Component<Refs>}
 */
class SscDeliveryEstimate extends Component {
  requiredRefs = ['main', 'sub', 'config'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** @type {any} */
  #config = null;

  /** @type {Record<string, { a: boolean, s: boolean, p: boolean }>} */
  #variants = {};

  #country = '';
  /** Fake clock offset for ?ssc_now (ms), 0 on the real clock */
  #clockOffset = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (!this.#readConfig()) return;
    this.#applyQaOverrides();

    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, {
      signal,
    });

    // Background tabs throttle the minute timer: catch up when the tab is shown again
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') this.#render();
      },
      { signal }
    );

    this.#render();
    this.#resolveCountry().then((country) => {
      if (signal.aborted) return;
      this.#country = country;
      this.#render();
      this.#schedule();
    });
  }

  /** A section re-render morphs the Liquid fallback back in: fill the lines again */
  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
    clearTimeout(this.#timer);
  }

  /** @returns {boolean} */
  #readConfig() {
    try {
      const raw = JSON.parse(this.refs.config.textContent ?? '{}');
      const countries = String(raw.countries ?? '')
        .toUpperCase()
        .split(/[\s,;]+/)
        .filter(Boolean);
      /** @type {Record<string, number>} */
      const transit = {};
      if (countries.includes('DE')) transit.DE = Number(raw.transitDE) || 0;
      if (countries.includes('AT')) transit.AT = Number(raw.transitAT) || 0;

      this.#variants = raw.variants ?? {};
      this.#config = {
        sameDay: Boolean(raw.sameDay),
        bufferDays: Number(raw.bufferDays) || 0,
        cutoffHour: Number(raw.cutoffHour) || 13,
        weekdays: parseWeekdays(raw.weekdays),
        transit,
        peakDays: Number(raw.peakDays) || 0,
        warehouseHolidays: parseDates(raw.warehouseHolidays),
        countryHolidays: { DE: parseDates(raw.holidaysDE), AT: parseDates(raw.holidaysAT) },
        strings: raw.strings ?? {},
        locale: raw.locale || 'en-GB',
      };
      return true;
    } catch (error) {
      console.warn('[ssc-delivery-estimate] Invalid config:', error);
      return false;
    }
  }

  #applyQaOverrides() {
    if (!('qa' in this.dataset)) return;
    const params = new URLSearchParams(window.location.search);

    const now = params.get('ssc_now')?.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{1,2})(?::(\d{2}))?)?$/);
    if (now) {
      const [, y, m, d, h = '12', min = '0'] = now;
      const fake = zonedToInstant(Number(y), Number(m), Number(d), Number(h), Number(min), WAREHOUSE_TZ);
      this.#clockOffset = fake - Date.now();
    }

    const country = params.get('ssc_country')?.trim().toUpperCase();
    if (country && /^[A-Z]{2}$/.test(country)) this.#country = country;

    const mode = params.get('ssc_mode');
    if (mode === 'sameday') this.#config.sameDay = true;
    if (mode === 'safe') this.#config.sameDay = false;

    if (params.get('ssc_delivery_test') === '1') this.#runTests();
  }

  async #runTests() {
    try {
      const { runDeliveryTests, DEFAULT_CONFIG } = await import('./ssc-delivery-tests.js');
      const { passed, failed, rows } = runDeliveryTests({
        ...DEFAULT_CONFIG,
        warehouseHolidays: this.#config.warehouseHolidays,
        countryHolidays: this.#config.countryHolidays,
        cutoffHour: this.#config.cutoffHour,
        weekdays: this.#config.weekdays,
        transit: this.#config.transit,
      });
      console.table(rows);
      console[failed ? 'error' : 'info'](`[ssc-delivery-estimate] tests: ${passed} passed, ${failed} failed`);
    } catch (error) {
      console.warn('[ssc-delivery-estimate] Tests failed to load:', error);
    }
  }

  /** @returns {Promise<string>} */
  async #resolveCountry() {
    if (this.#country) return this.#country;
    if (!('designMode' in this.dataset)) {
      try {
        const { shipStatus } = await import('@theme/ssc-ship-check');
        const { code } = await shipStatus;
        if (code) return code;
      } catch {
        /* fall back below */
      }
    }
    return String(/** @type {any} */ (window).Shopify?.country ?? '').toUpperCase();
  }

  #render() {
    if (!this.#config) return;
    const { main, sub } = this.refs;
    const { main: mainText, sub: subText } = computeEstimate({
      now: Date.now() + this.#clockOffset,
      country: this.#country,
      config: this.#config,
    });
    if (main.textContent !== mainText) main.textContent = mainText;
    if (sub.textContent !== subText) sub.textContent = subText;
    sub.hidden = !subText;
  }

  /** Re-render on the next minute boundary, then every minute */
  #schedule() {
    clearTimeout(this.#timer);
    const now = Date.now() + this.#clockOffset;
    this.#timer = setTimeout(() => {
      this.#render();
      this.#schedule();
    }, 60000 - (now % 60000) + 50);
  }

  /**
   * Shows the row only for a variant that ships now.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || !this.isConnected) return;
        const { newProduct, productId, resource } = detail;
        if (!newProduct && productId && String(productId) !== this.dataset.productId) return;
        if (!resource) return;

        const known = this.#variants[String(resource.id)];
        const ships = known ? known.a && known.s && !known.p : resource.available !== false && resource.requires_shipping !== false;
        const row = this.closest('.ssc-delivery');
        if (row instanceof HTMLElement) row.hidden = !ships;
        if (ships) this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ssc-delivery-estimate] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('ssc-delivery-estimate')) customElements.define('ssc-delivery-estimate', SscDeliveryEstimate);
